import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { Card, Spinner, Button } from 'react-bootstrap'
import ChatCard from './chat-card'
import { Chat } from './types'

interface ChatsListCardProps {
  chats: Chat[]
  chatsLoading: boolean
  onSelectChat: (chatId: string) => void
  hasMoreChats: boolean
  onLoadMoreChats: () => void
  selectedChatId?: string | null
  filterType: 'all' | 'no_response' | 'response' | 'issues'
  onFilterChange: (
    filter: 'all' | 'no_response' | 'response' | 'issues'
  ) => void
  isLoadingMore?: boolean
  onBack?: () => void
}

const FILTERS: {
  key: 'all' | 'no_response' | 'response' | 'issues'
  label: string
}[] = [
  { key: 'all', label: 'All' },
  { key: 'no_response', label: 'No response' },
  { key: 'response', label: 'Response' },
]

const ChatsListCard: React.FC<ChatsListCardProps> = ({
  chats,
  chatsLoading,
  onSelectChat,
  hasMoreChats,
  onLoadMoreChats,
  selectedChatId,
  filterType,
  onFilterChange,
  isLoadingMore = false,
  onBack,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const chatRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Sort chats by most recent (API handles filtering now)
  const sortedChats = useMemo(() => {
    return [...chats].sort(
      (a, b) =>
        new Date(b.last_message.sent_at).getTime() -
        new Date(a.last_message.sent_at).getTime()
    )
  }, [chats])

  // Scroll to selected chat when it changes or when chats are loaded
  useEffect(() => {
    if (selectedChatId && chatRefs.current[selectedChatId]) {
      chatRefs.current[selectedChatId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [selectedChatId, sortedChats])

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    if (isLoadingMore) return

    if (!hasMoreChats) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const scrollPosition = scrollTop + clientHeight
    const threshold = scrollHeight - 100 // Load when 100px from bottom

    if (scrollPosition >= threshold) {
      onLoadMoreChats()
    }
  }, [isLoadingMore, hasMoreChats, onLoadMoreChats])

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    return () => container.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return (
    <Card
      className="mt-4"
      style={{
        height: 'clamp(400px, 70vh, 700px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '400px',
      }}
    >
      <Card.Header
        className={onBack ? 'd-flex align-items-center' : ''}
        style={{ padding: 'clamp(10px, 3vw, 10px)' }}
      >
        {onBack && (
          <Button
            variant="link"
            onClick={onBack}
            className="me-3 d-lg-none p-0"
            style={{
              fontSize: 'clamp(20px, 4vw, 24px)',
              color: '#4ea6ff',
            }}
          >
            <ion-icon name="chevron-back-outline" />
          </Button>
        )}
        <h4 className="mb-2" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>
          Messages
        </h4>
      </Card.Header>
      <div className="mt-3 justify-content-between align-items-center">
        <div className="mb-2" style={{ padding: '0 clamp(8px, 2vw, 16px)' }}>
          <div className="btn-group w-100" role="group">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className="btn fw-bold"
                onClick={() => onFilterChange(f.key)}
                style={{
                  flex: 1,
                  backgroundColor:
                    filterType === f.key ? '#4EA6FF' : 'transparent',
                  color: filterType === f.key ? 'white' : '#4EA6FF',
                  border: '1px solid #4EA6FF',
                  fontWeight: 'bold',
                  fontSize: 'clamp(11px, 2.5vw, 14px)',
                  padding: 'clamp(6px, 1.5vw, 10px) clamp(8px, 2vw, 12px)',
                  overflow: 'hidden',
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <Card.Body
        ref={scrollContainerRef}
        className="flex-grow-1"
        style={{ overflowY: 'auto', padding: '0' }}
      >
        {chatsLoading && chats.length === 0 ? (
          <div className="text-center p-4">
            <Spinner animation="border" />
            <p className="mt-2">Loading conversations...</p>
          </div>
        ) : sortedChats.length === 0 ? (
          <div className="text-center p-4">
            <p>
              {filterType !== 'all'
                ? 'No conversations match your filter criteria'
                : 'No conversations found'}
            </p>
          </div>
        ) : (
          <div className="p-2">
            {sortedChats.map((chat) => (
              <div
                key={chat.id}
                ref={(el) => {
                  chatRefs.current[chat.id] = el
                }}
              >
                <ChatCard
                  chat={chat}
                  onSelect={() => onSelectChat(chat.id.toString())}
                  isSelected={selectedChatId === chat.id.toString()}
                />
              </div>
            ))}
            {hasMoreChats && isLoadingMore && (
              <div className="text-center p-3">
                <Spinner animation="border" size="sm" />
                <p className="mt-2 mb-0">Loading more chats...</p>
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

export default ChatsListCard
